/**
 * CommunicationList Component
 *
 * Displays a chronological list of all customer communications grouped by type.
 * Shows communication type, title, description, and timestamp.
 *
 * Requirements: 10.1, 10.2 - Display chronological list grouped by type
 */

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Badge,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '@bloomcode/ui';
import { Ticket, FileText, Bell, Mail, MessageSquare, Calendar } from '@bloomcode/icons';
import type { CommunicationType } from '@/components/dashboard';

export interface CommunicationItem {
  id: string;
  type: CommunicationType;
  title: string;
  description?: string;
  content?: string;
  date: string;
  isRead?: boolean;
  link?: string;
  metadata?: {
    ticketNumber?: string;
    invoiceNumber?: string;
    status?: string;
  };
}

interface CommunicationListProps {
  communications: CommunicationItem[];
  isLoading?: boolean;
  onCommunicationClick?: (communication: CommunicationItem) => void;
  groupByType?: boolean;
}

function CommunicationListSkeleton() {
  return (
    <Card>
      <CardHeader>
        <div className="h-5 w-44 animate-pulse rounded bg-muted" />
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="flex items-start gap-3 rounded-lg border p-4">
              <div className="h-10 w-10 animate-pulse rounded-full bg-muted" />
              <div className="flex-1 space-y-2">
                <div className="h-4 w-3/4 animate-pulse rounded bg-muted" />
                <div className="h-3 w-1/2 animate-pulse rounded bg-muted" />
                <div className="h-3 w-1/4 animate-pulse rounded bg-muted" />
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

const communicationIcons: Record<CommunicationType, React.ReactNode> = {
  ticket: <Ticket className="h-4 w-4" />,
  invoice: <FileText className="h-4 w-4" />,
  announcement: <Bell className="h-4 w-4" />,
  email: <Mail className="h-4 w-4" />,
  message: <MessageSquare className="h-4 w-4" />,
};

const communicationColors: Record<CommunicationType, string> = {
  ticket: 'bg-primary/10 text-primary',
  invoice: 'bg-success/10 text-success',
  announcement: 'bg-accent/10 text-accent',
  email: 'bg-warning/10 text-warning',
  message: 'bg-warning/10 text-warning',
};

const communicationLabels: Record<CommunicationType, string> = {
  ticket: 'Support Ticket',
  invoice: 'Invoice',
  announcement: 'Announcement',
  email: 'Email',
  message: 'Message',
};

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return formatDate(dateString);
}

interface CommunicationItemCardProps {
  communication: CommunicationItem;
  onClick?: () => void;
}

function CommunicationItemCard({ communication, onClick }: CommunicationItemCardProps) {
  const icon = communicationIcons[communication.type];
  const colorClass = communicationColors[communication.type];
  const label = communicationLabels[communication.type];

  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-start gap-4 rounded-lg border p-4 text-left transition-colors hover:bg-muted/50"
    >
      <div
        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${colorClass}`}
      >
        {icon}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant="outline" className="text-xs">
            {label}
          </Badge>
          {communication.metadata?.ticketNumber && (
            <span className="text-xs font-mono text-muted-foreground">
              #{communication.metadata.ticketNumber}
            </span>
          )}
          {communication.metadata?.invoiceNumber && (
            <span className="text-xs font-mono text-muted-foreground">
              #{communication.metadata.invoiceNumber}
            </span>
          )}
          {communication.metadata?.status && (
            <Badge variant="secondary" className="text-xs">
              {communication.metadata.status}
            </Badge>
          )}
          {!communication.isRead && <span className="h-2 w-2 shrink-0 rounded-full bg-primary" />}
        </div>

        <p className={`mt-1 font-medium truncate ${!communication.isRead ? 'font-semibold' : ''}`}>
          {communication.title}
        </p>

        {communication.description && (
          <p className="mt-1 text-sm text-muted-foreground line-clamp-2">
            {communication.description}
          </p>
        )}

        <div className="mt-2 flex items-center gap-1 text-xs text-muted-foreground">
          <Calendar className="h-3 w-3" />
          <span>{formatRelativeTime(communication.date)}</span>
        </div>
      </div>
    </button>
  );
}

/**
 * Groups communications by type
 */
function groupCommunicationsByType(
  communications: CommunicationItem[]
): Record<CommunicationType, CommunicationItem[]> {
  const groups: Record<CommunicationType, CommunicationItem[]> = {
    ticket: [],
    invoice: [],
    announcement: [],
    email: [],
    message: [],
  };

  communications.forEach((comm) => {
    if (groups[comm.type]) {
      groups[comm.type].push(comm);
    }
  });

  return groups;
}

interface GroupedListProps {
  communications: CommunicationItem[];
  onCommunicationClick?: (communication: CommunicationItem) => void;
}

function GroupedList({ communications, onCommunicationClick }: GroupedListProps) {
  const grouped = groupCommunicationsByType(communications);
  const types: CommunicationType[] = ['ticket', 'invoice', 'announcement', 'email', 'message'];

  // Filter to only show types that have communications
  const activeTypes = types.filter((type) => grouped[type].length > 0);

  if (activeTypes.length === 0) {
    return (
      <div className="py-12 text-center">
        <MessageSquare className="mx-auto h-12 w-12 text-muted-foreground/50" />
        <p className="mt-2 text-muted-foreground">No communications found</p>
        <p className="text-sm text-muted-foreground">Your communications will appear here.</p>
      </div>
    );
  }

  return (
    <Tabs defaultValue={activeTypes[0]} className="w-full">
      <TabsList className="flex-wrap h-auto gap-1 mb-4">
        {activeTypes.map((type) => (
          <TabsTrigger key={type} value={type} className="text-sm">
            <span className="flex items-center gap-2">
              {communicationIcons[type]}
              {communicationLabels[type]}
              <Badge variant="secondary" className="ml-1 text-xs">
                {grouped[type].length}
              </Badge>
            </span>
          </TabsTrigger>
        ))}
      </TabsList>

      {activeTypes.map((type) => (
        <TabsContent key={type} value={type} className="mt-0">
          <div className="space-y-2">
            {grouped[type].map((communication) => (
              <CommunicationItemCard
                key={communication.id}
                communication={communication}
                onClick={() => onCommunicationClick?.(communication)}
              />
            ))}
          </div>
        </TabsContent>
      ))}
    </Tabs>
  );
}

interface ChronologicalListProps {
  communications: CommunicationItem[];
  onCommunicationClick?: (communication: CommunicationItem) => void;
}

function ChronologicalList({ communications, onCommunicationClick }: ChronologicalListProps) {
  if (communications.length === 0) {
    return (
      <div className="py-12 text-center">
        <MessageSquare className="mx-auto h-12 w-12 text-muted-foreground/50" />
        <p className="mt-2 text-muted-foreground">No communications found</p>
        <p className="text-sm text-muted-foreground">Your communications will appear here.</p>
      </div>
    );
  }

  // Sort by date descending (most recent first)
  const sortedCommunications = [...communications].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
  );

  return (
    <div className="space-y-2">
      {sortedCommunications.map((communication) => (
        <CommunicationItemCard
          key={communication.id}
          communication={communication}
          onClick={() => onCommunicationClick?.(communication)}
        />
      ))}
    </div>
  );
}

export function CommunicationList({
  communications,
  isLoading,
  onCommunicationClick,
  groupByType = true,
}: CommunicationListProps) {
  if (isLoading) {
    return <CommunicationListSkeleton />;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Communications</CardTitle>
      </CardHeader>
      <CardContent>
        {groupByType ? (
          <GroupedList
            communications={communications}
            onCommunicationClick={onCommunicationClick}
          />
        ) : (
          <ChronologicalList
            communications={communications}
            onCommunicationClick={onCommunicationClick}
          />
        )}
      </CardContent>
    </Card>
  );
}

export default CommunicationList;
